import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Card } from '@/components/layout/Card/Card';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';

import {
  auditLogsOptions,
  brandingOptions,
  collaboratorsOptions,
  dispositiveProvisionsOptions,
  employeesOptions,
  householdsOptions,
  presentationTemplatesOptions,
  rolesOptions,
  waterfallOptions,
} from './RoleViewer.constants';
import { RoleFormData, RoleFormDataPaths } from './RoleViewer.types';
import { generateSlugFromName } from './RoleViewer.utils';

export interface RoleFormProps {
  variant: 'create' | 'edit';
  onDeleteRole?: () => void;
}

export function RoleForm({ variant, onDeleteRole }: RoleFormProps) {
  const { control, watch, setValue } = useFormContext<RoleFormData>();
  const nameValue = watch('name');

  useEffect(() => {
    if (variant === 'create' && nameValue) {
      const generatedSlug = generateSlugFromName(nameValue);
      setValue('slug', generatedSlug, { shouldValidate: true });
    }
  }, [nameValue, variant, setValue]);

  const slugHelpText =
    "A unique key to reference the role. Can't be edited after creation.";

  return (
    <Stack spacing={2}>
      {/* Name Field */}
      <Stack spacing={2}>
        <FormAwareTextInput
          fieldName={'name' as const satisfies RoleFormDataPaths}
          label="Name"
          control={control}
          required
        />

        <FormAwareTextInput
          fieldName={'slug' as const satisfies RoleFormDataPaths}
          label="Slug"
          control={control}
          required
          disabled={variant === 'edit'}
          helpText={slugHelpText}
          validation={{
            pattern: (value) => {
              if (!value) return undefined;
              if (typeof value !== 'string') return undefined;

              const slugPattern = /^[a-z]([a-z-]*[a-z])?$/;
              if (!slugPattern.test(value)) {
                return 'Slug must start and end with a lowercase letter, and contain only lowercase letters and hyphens';
              }
              return undefined;
            },
          }}
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FormAwareCheckbox
            fieldName={'isDefault' as const satisfies RoleFormDataPaths}
            label="Default role"
            control={control}
            contextualHelp={
              <PopperContent body="When a user is created through SSO, this role will be assigned to them by default. (If there is a specific entitlement, that is used instead.)" />
            }
          />
          {variant === 'edit' && onDeleteRole && (
            <Button
              variant="destructive-secondary"
              size="sm"
              onClick={onDeleteRole}
            >
              Delete role
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Access Controls */}
      <Stack spacing={2}>
        <Typography variant="title-s">Access controls</Typography>

        {/* General Properties Card */}
        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body-s" sx={{ fontWeight: 600 }}>
              General properties
            </Typography>
            <Stack spacing={0.5}>
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canActAsRelationshipOwner' as const satisfies RoleFormDataPaths
                }
                label="Can act as relationship owner"
                control={control}
                contextualHelp={
                  <PopperContent body="Can be assigned as the relationship owner for any household they have access to." />
                }
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.hasAllHouseholdsAccess' as const satisfies RoleFormDataPaths
                }
                label="Has all-households access"
                contextualHelp={
                  <PopperContent body="Has access to all households by default. When disabled, the user must be explicitly assigned to a household to have access." />
                }
                control={control}
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canModifyGlobalCategories' as const satisfies RoleFormDataPaths
                }
                label="Can modify global categories"
                contextualHelp={
                  <PopperContent body="Can create and update asset classes (and associated growth rates), as well as balance sheet categories." />
                }
                control={control}
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canModifyPrompts' as const satisfies RoleFormDataPaths
                }
                label="Can modify prompts"
                contextualHelp={
                  <PopperContent body="Can create and update prompts on the platform." />
                }
                control={control}
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canAccessTenantReporting' as const satisfies RoleFormDataPaths
                }
                label="Can access tenant reporting"
                contextualHelp={
                  <PopperContent body="Can generate and download tenant-level reports." />
                }
                control={control}
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canApproveLuminarySupportTeamAccess' as const satisfies RoleFormDataPaths
                }
                label="Can approve Luminary support team access"
                contextualHelp={
                  <PopperContent body="Can configure Luminary support access policy for this tenant." />
                }
                control={control}
              />
              <FormAwareCheckbox
                fieldName={
                  'generalProperties.canApproveClientPresentations' as const satisfies RoleFormDataPaths
                }
                label="Can approve client presentations"
                contextualHelp={
                  <PopperContent body="Can approve client presentations. Only applies when the specialist approval workflow is turned on." />
                }
                control={control}
              />
            </Stack>
          </Stack>
        </Card>

        {/* Branding Card */}
        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Branding"
              fieldName={'branding' as const satisfies RoleFormDataPaths}
              options={brandingOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        {/* Employees Card */}
        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Employees"
              fieldName={'employees' as const satisfies RoleFormDataPaths}
              options={employeesOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Collaborators"
              fieldName={'collaborators' as const satisfies RoleFormDataPaths}
              options={collaboratorsOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Households"
              fieldName={'households' as const satisfies RoleFormDataPaths}
              options={householdsOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Waterfall access"
              fieldName={'waterfall' as const satisfies RoleFormDataPaths}
              options={waterfallOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Dispositive provisions"
              fieldName={
                'dispositiveProvisions' as const satisfies RoleFormDataPaths
              }
              options={dispositiveProvisionsOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Audit logs"
              fieldName={'auditLogs' as const satisfies RoleFormDataPaths}
              options={auditLogsOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Roles"
              fieldName={'roles' as const satisfies RoleFormDataPaths}
              options={rolesOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>

        <Card variant="filled-outline" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareRadioGroup
              label="Shared presentation templates"
              fieldName={
                'presentationTemplates' as const satisfies RoleFormDataPaths
              }
              options={presentationTemplatesOptions}
              control={control}
              columnSpacing={1}
              row={false}
            />
          </Stack>
        </Card>
      </Stack>
    </Stack>
  );
}
